import * as React from 'react';
import { useState, type ReactNode } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useAvailableSections } from '../lib/use-available-sections.js';
import type { MeResponse } from '@wit-support/admin-contracts';
import { Button, DropdownMenu, Separator, Sheet, Tooltip, toast } from '@wit-support/ui';
import {
  AdminAvatar,
  RoleBadge,
  ADMIN_WORKSPACE_BY_ID,
  BreadcrumbDetailProvider,
  PageChromeProvider,
  type AdminClientModule,
} from '@wit-support/ui-domain';
import {
  Bell,
  BellOff,
  ChevronDown,
  ChevronsLeft,
  ChevronsRight,
  KeyRound,
  LogOut,
  Menu,
} from 'lucide-react';
import { ChangePasswordDialog } from '@wit-support/admin-module-admins/client';
import {
  conversationsModule,
  INBOX_LAST_CONVERSATION_KEY,
} from '@wit-support/admin-module-conversations/client';
import { clearPersistedScreenState } from '../lib/persisted-screen-state.js';
import { MODULES } from '../app.js';
import { API_ORIGIN } from '../lib/api-origin.js';
import { externalNavLinksFor } from '../lib/external-links.js';
import { env } from '../env.js';
import {
  availableWorkspaces,
  modulesInWorkspace,
  navModulesFor,
  readStoredWorkspace,
  resolveActiveWorkspace,
  workspaceForPath,
  writeStoredWorkspace,
} from '../lib/workspace-nav.js';
import { shellChromeOf, shellLayoutClasses, shellLayoutOf } from '../lib/route-layout.js';
import { ExternalNavLinks, SidebarNav } from './shell-nav.js';
import { SHELL_MAIN_ID, ShellHeader, ShellPageTitle, SkipToContentLink } from './shell-header.js';
import {
  getPushStatus,
  subscribe as subscribePush,
  unsubscribe as unsubscribePush,
  PushNeedsInstallError,
  PushDeniedError,
  type PushStatus,
} from '../pwa/push.js';

const SIDEBAR_COLLAPSED_KEY = 'wit-admin:sidebar-collapsed';

/**
 * Read the persisted desktop-sidebar collapsed preference. Defaults to
 * collapsed (`true`) when nothing has been stored yet or storage is
 * unavailable (e.g. SSR / privacy mode).
 */
function readSidebarCollapsed(): boolean {
  try {
    const raw = window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    if (raw === null) return true;
    return raw === 'true';
  } catch {
    return true;
  }
}

/**
 * The operator's last open conversation id, or null. Used to point the «Чаты»
 * nav item at `/conversations/<id>` so returning to the inbox from another
 * module resumes the open chat — WITHOUT a mount-time redirect that would
 * hijack the browser Back button.
 */
function readInboxLastConversationId(): string | null {
  try {
    return window.localStorage.getItem(INBOX_LAST_CONVERSATION_KEY);
  } catch {
    return null;
  }
}

/**
 * «Чаты» в сайдбаре возвращают к последнему открытому диалогу. Подсветка всё
 * равно по префиксу модуля, так что пункт активен на любом /conversations/*.
 */
function inboxResumeHref(m: AdminClientModule): string | null {
  if (m.id !== conversationsModule.id) return null;
  const lastConversationId = readInboxLastConversationId();
  return lastConversationId ? `/${m.id}/${lastConversationId}` : null;
}

/**
 * App layout shell. Responsive breakpoint is `lg` (1024px):
 *  - `< lg`: single column; nav lives in a left-slide Sheet opened via the
 *    header hamburger. User menu also moves into the header. The Sheet always
 *    shows the full (labelled) nav — the collapse toggle is desktop-only.
 *  - `>= lg`: permanent sidebar that can be collapsed to an icon-only rail
 *    (64px) or expanded (240px). The choice persists in localStorage and
 *    defaults to collapsed.
 *
 * The Sheet variant re-uses the same nav markup as the desktop sidebar so
 * there's no drift between the two forms.
 */
export function AppShell({ me, children }: { me: MeResponse; children: ReactNode }) {
  const [pwOpen, setPwOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(readSidebarCollapsed);
  const [pushStatus, setPushStatus] = useState<PushStatus | null>(null);
  const [pushBusy, setPushBusy] = useState(false);
  const path = useRouterState({ select: (s) => s.location.pathname });
  // Полноэкранный экран (редактор пайплайна) получает всю область под шапкой
  // ровно в высоту окна; обычная страница прокручивается в <main>.
  const layout = useRouterState({ select: (s) => shellLayoutOf(s.matches) });
  const layoutClasses = shellLayoutClasses(layout);
  // Кто рисует шапку экрана: сама страница ('page', по умолчанию) или навбар
  // ('shell' — тулбар страницы в навбаре, скрытый h1), см. `ModuleRouteChrome`.
  const chrome = useRouterState({ select: (s) => shellChromeOf(s.matches) });
  // Фактическая высота навбара → `--shell-header-h` (экраны на всю высоту окна
  // считают от неё, а не от «56px»; ниже md у 'shell' бывает вторая строка).
  const [headerHeight, setHeaderHeight] = useState(56);
  const queryClient = useQueryClient();

  // Persist the desktop-sidebar collapse choice across sessions.
  React.useEffect(() => {
    try {
      window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
    } catch {
      // Best-effort — never break rendering on storage failure.
    }
  }, [collapsed]);

  // Web Push carries support-inbox previews; only operators who can open the
  // inbox get the toggle (a scoped role like `marketing` cannot — admin-api
  // refuses `/api/push/*` for it and the worker never delivers to it).
  const canReceivePush = me.permissions.includes('conversations.list');

  // Hydrate push status once on mount so the menu label reflects reality.
  React.useEffect(() => {
    if (!canReceivePush) return;
    getPushStatus()
      .then((s) => setPushStatus(s))
      .catch(() => setPushStatus({ kind: 'unsupported' }));
  }, [canReceivePush]);

  async function handleEnablePush(): Promise<void> {
    if (pushBusy) return;
    setPushBusy(true);
    try {
      await subscribePush();
      setPushStatus(await getPushStatus());
      toast.success('Notifications enabled');
    } catch (err) {
      if (err instanceof PushNeedsInstallError) {
        toast.info('Install this app to your Home Screen first, then try again.');
      } else if (err instanceof PushDeniedError) {
        toast.error('Notification permission denied');
      } else {
        toast.error('Failed to enable notifications');
      }
    } finally {
      setPushBusy(false);
    }
  }

  async function handleDisablePush(): Promise<void> {
    if (pushBusy) return;
    setPushBusy(true);
    try {
      await unsubscribePush();
      setPushStatus(await getPushStatus());
      toast.success('Notifications disabled');
    } finally {
      setPushBusy(false);
    }
  }

  const navModules = useAvailableSections(navModulesFor(MODULES, me.permissions));
  const workspaces = availableWorkspaces(navModules);
  // Активное пространство ВЫВОДИТСЯ из маршрута: прямая ссылка на
  // /conversations/… обязана открыться в «Поддержке». localStorage хранит
  // только «куда приземлиться на /».
  const activeWorkspaceId = resolveActiveWorkspace({
    routeWorkspace: workspaceForPath(MODULES, path),
    stored: readStoredWorkspace(),
    available: workspaces,
  });
  const activeWorkspace = activeWorkspaceId ? ADMIN_WORKSPACE_BY_ID[activeWorkspaceId] : null;
  const visibleModules = activeWorkspaceId
    ? modulesInWorkspace(navModules, activeWorkspaceId)
    : navModules;
  // Подразделы активного модуля в режиме 'tabs' (по умолчанию) живут в навбаре —
  // одном на всю админку, а не в теле каждой страницы (см. `ShellHeader`).
  const currentModule = navModules.find((m) => path === `/${m.id}` || path.startsWith(`/${m.id}/`));

  async function signOut(): Promise<void> {
    try {
      await fetch(`${env.VITE_ADMIN_API_URL}/auth/sign-out`, {
        method: 'POST',
        credentials: 'include',
      });
    } finally {
      // Belt-and-suspenders: drop all in-memory authed query data and any
      // stale SW runtime caches so a subsequent user on a shared device
      // cannot read a prior session's data from cache.
      queryClient.clear();
      try {
        if ('caches' in window) {
          const keys = await caches.keys();
          await Promise.all(keys.filter((k) => /api/i.test(k)).map((k) => caches.delete(k)));
        }
      } catch {
        // Cache purge is best-effort — never block the redirect.
      }
      // Drop persisted screen-state (open chat, inbox filters incl. the search
      // query, chargebacks case-list filters) so the next operator on a shared
      // device does not inherit the prior session's view. The key list lives in
      // one place — see `persisted-screen-state.ts`.
      clearPersistedScreenState();
      window.location.href = '/';
    }
  }

  // Переключатель сам называет пространство, поэтому заголовок группы под ним
  // повторял бы то же слово дважды подряд. Заголовок остаётся ТОЛЬКО как
  // запасной носитель названия там, где переключателя нет: у оператора с
  // доступом к единственному пространству (см. `renderWorkspaceSwitcher`), а
  // также когда прямая ссылка привела в пространство вне списка доступных.
  const showsWorkspaceSwitcher = activeWorkspace !== null && workspaces.length >= 2;
  const sectionLabel = showsWorkspaceSwitcher ? null : (activeWorkspace?.label ?? null);

  // Desktop sidebar nav (respects collapse); mobile Sheet always shows full nav.
  const desktopNav = (
    <SidebarNav
      modules={visibleModules}
      sectionLabel={sectionLabel}
      currentPath={path}
      onNavigate={() => setNavOpen(false)}
      collapsed={collapsed}
      moduleHref={inboxResumeHref}
    />
  );
  const mobileNav = (
    <SidebarNav
      modules={visibleModules}
      sectionLabel={sectionLabel}
      currentPath={path}
      onNavigate={() => setNavOpen(false)}
      collapsed={false}
      moduleHref={inboxResumeHref}
    />
  );

  // Ссылки вон из SPA («Документация MCP» на admin-api) — по правам и
  // признакам сервера из `me`, под списком разделов в любом пространстве (у
  // маркетолога тоже).
  const externalLinks = externalNavLinksFor(me, API_ORIGIN);
  const renderExternalLinks = (compact: boolean) =>
    externalLinks.length > 0 ? (
      <div className="mt-auto pt-2">
        <ExternalNavLinks
          links={externalLinks}
          collapsed={compact}
          onNavigate={() => setNavOpen(false)}
        />
      </div>
    ) : null;

  /**
   * Переключатель пространств. Клик НЕ хранит «активное пространство» — он
   * ведёт на первый модуль выбранного пространства и запоминает выбор как
   * «куда приземлиться на /». Дальше активность выводится из маршрута сама.
   *
   * При единственном доступном пространстве не рендерится вовсе: выпадающий
   * список из одного пункта — это шум. Ровно в этом случае название
   * пространства показывает заголовок группы в `NavList` — условие одно на
   * оба места (`showsWorkspaceSwitcher`), чтобы они не разъехались.
   */
  const renderWorkspaceSwitcher = (compact: boolean) => {
    if (!activeWorkspace || !showsWorkspaceSwitcher) return null;
    const ActiveIcon = activeWorkspace.icon;
    return (
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={compact ? 'w-full justify-center px-0' : 'w-full justify-start gap-2'}
            aria-label={`Рабочее пространство: ${activeWorkspace.label}`}
            title={compact ? activeWorkspace.label : undefined}
          >
            <ActiveIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
            {compact ? null : (
              <>
                <span className="min-w-0 flex-1 truncate text-left">{activeWorkspace.label}</span>
                <ChevronDown className="h-4 w-4 shrink-0 opacity-60" aria-hidden="true" />
              </>
            )}
          </Button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content align="start" side="bottom">
          <DropdownMenu.Label>Рабочее пространство</DropdownMenu.Label>
          <DropdownMenu.Separator />
          {workspaces.map((ws) => {
            const first = modulesInWorkspace(navModules, ws.id)[0];
            if (!first) return null;
            const Icon = ws.icon;
            // Через переменную, а не инлайном: у инлайнового шаблона `to` берёт
            // контекстный тип роутера и выводит литерал `/${string}`, который
            // реестр маршрутов не принимает. Тот же приём в `NavList` ниже.
            const href = `/${first.id}`;
            return (
              <DropdownMenu.Item key={ws.id} asChild>
                <Link
                  to={href}
                  onClick={() => {
                    writeStoredWorkspace(ws.id);
                    setNavOpen(false);
                  }}
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                  {ws.label}
                </Link>
              </DropdownMenu.Item>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    );
  };

  const renderUserMenu = (compact: boolean) => (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={compact ? 'w-full justify-center px-0' : 'gap-2'}
          aria-label={compact ? me.admin.displayName : undefined}
          title={compact ? me.admin.displayName : undefined}
        >
          <AdminAvatar admin={me.admin} size="sm" />
          {compact ? null : (
            <>
              <span className="hidden min-w-0 max-w-[140px] truncate text-left sm:inline">
                {me.admin.displayName}
              </span>
              <ChevronDown className="h-4 w-4 opacity-60" aria-hidden="true" />
            </>
          )}
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="end" side="bottom">
        <DropdownMenu.Label>
          <div className="flex items-center gap-2">
            <span className="truncate">{me.admin.displayName}</span>
            <RoleBadge role={me.role} size="sm" />
          </div>
        </DropdownMenu.Label>
        <DropdownMenu.Separator />
        {canReceivePush && pushStatus && pushStatus.kind !== 'unsupported' ? (
          pushStatus.kind === 'subscribed' ? (
            <DropdownMenu.Item onSelect={() => void handleDisablePush()} disabled={pushBusy}>
              <BellOff className="h-4 w-4" /> Disable notifications
            </DropdownMenu.Item>
          ) : (
            <DropdownMenu.Item onSelect={() => void handleEnablePush()} disabled={pushBusy}>
              <Bell className="h-4 w-4" />
              {pushStatus.kind === 'needs-install'
                ? 'Add to Home Screen first'
                : pushStatus.kind === 'denied'
                  ? 'Notifications blocked'
                  : 'Enable notifications'}
            </DropdownMenu.Item>
          )
        ) : null}
        <DropdownMenu.Item onSelect={() => setPwOpen(true)}>
          <KeyRound className="h-4 w-4" /> Change password
        </DropdownMenu.Item>
        <DropdownMenu.Item onSelect={() => void signOut()}>
          <LogOut className="h-4 w-4" /> Sign out
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );

  return (
    <Tooltip.SharedProvider>
      <BreadcrumbDetailProvider>
        <PageChromeProvider mode={chrome}>
          <div
            style={{ '--shell-header-h': `${headerHeight}px` } as React.CSSProperties}
            className={[
              'grid bg-[var(--color-surface-canvas)] text-[var(--color-text-primary)] transition-[grid-template-columns] duration-200 ease-out',
              'pt-[env(safe-area-inset-top)] pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)] pb-[env(safe-area-inset-bottom)]',
              collapsed ? 'lg:grid-cols-[64px_1fr]' : 'lg:grid-cols-[240px_1fr]',
              layoutClasses.root,
            ].join(' ')}
          >
            {/* Первая остановка Tab — до сайдбара (см. `SkipToContentLink`). */}
            <SkipToContentLink />
            <aside
              className={[
                'hidden lg:flex lg:flex-col lg:border-r lg:border-[var(--color-border-default)] lg:bg-[var(--color-surface-sunken)]',
                // Липкий сайдбар: страница прокручивается окном (`min-h-dvh`), и без
                // `sticky` меню уезжало вверх вместе с длинной формой — слева оставалась
                // пустая колонка. Высота — окно, своя прокрутка у списка разделов.
                'lg:sticky lg:top-0 lg:h-dvh lg:self-start',
                'overflow-hidden transition-[width] duration-200 ease-out',
                collapsed ? 'lg:w-[64px]' : 'lg:w-[240px]',
              ].join(' ')}
            >
              <SidebarHeader collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} />
              <div
                className={[
                  'flex-1 flex flex-col gap-2 py-4 overflow-auto',
                  collapsed ? 'px-2' : 'px-3',
                ].join(' ')}
              >
                {renderWorkspaceSwitcher(collapsed)}
                {desktopNav}
                {renderExternalLinks(collapsed)}
              </div>
              <Separator />
              <div className={collapsed ? 'p-2' : 'p-3'}>{renderUserMenu(collapsed)}</div>
            </aside>

            <div className="flex flex-col min-h-0 min-w-0">
              <ShellHeader
                chrome={chrome}
                modules={MODULES}
                navModules={navModules}
                path={path}
                currentModule={currentModule}
                onHeightChange={setHeaderHeight}
                navMenu={
                  <Sheet.Root open={navOpen} onOpenChange={setNavOpen}>
                    <Sheet.Trigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="lg:hidden"
                        aria-label="Открыть меню"
                      >
                        <Menu className="h-5 w-5" aria-hidden="true" />
                      </Button>
                    </Sheet.Trigger>
                    <Sheet.Content
                      side="left"
                      className="bg-[var(--color-surface-sunken)]"
                      aria-describedby={undefined}
                    >
                      <Sheet.Title>Навигация</Sheet.Title>
                      <SidebarHeader collapsed={false} />
                      <div className="flex flex-1 flex-col gap-2 overflow-auto px-3 py-4">
                        {renderWorkspaceSwitcher(false)}
                        {mobileNav}
                        {renderExternalLinks(false)}
                      </div>
                    </Sheet.Content>
                  </Sheet.Root>
                }
                userMenu={renderUserMenu(false)}
              />
              <main
                id={SHELL_MAIN_ID}
                tabIndex={-1}
                className={`${layoutClasses.main} focus:outline-none`}
              >
                <ShellPageTitle
                  chrome={chrome}
                  modules={MODULES}
                  navModules={navModules}
                  path={path}
                />
                {children}
              </main>
            </div>

            <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
          </div>
        </PageChromeProvider>
      </BreadcrumbDetailProvider>
    </Tooltip.SharedProvider>
  );
}

function SidebarHeader({
  collapsed,
  onToggle,
}: {
  collapsed: boolean;
  /** When provided, renders the collapse/expand toggle (desktop only). */
  onToggle?: () => void;
}): React.ReactElement {
  return (
    <div
      className={[
        'flex h-14 items-center border-b border-[var(--color-border-default)]',
        collapsed ? 'justify-center px-2' : 'gap-2 px-4',
      ].join(' ')}
    >
      <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-[var(--radius-2)] bg-[var(--color-brand-bg)] text-[11px] font-semibold text-[var(--color-brand-fg)]">
        W
      </div>
      {collapsed ? null : (
        <div className="flex-1 truncate text-sm font-semibold tracking-tight">WIT Admin</div>
      )}
      {onToggle ? (
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggle}
          className={collapsed ? 'mt-1 h-7 w-7 px-0' : 'h-7 w-7 px-0'}
          aria-label={collapsed ? 'Развернуть меню' : 'Свернуть меню'}
          title={collapsed ? 'Развернуть меню' : 'Свернуть меню'}
        >
          {collapsed ? (
            <ChevronsRight className="h-4 w-4" aria-hidden="true" />
          ) : (
            <ChevronsLeft className="h-4 w-4" aria-hidden="true" />
          )}
        </Button>
      ) : null}
    </div>
  );
}
